import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, RefreshCwOff } from 'lucide-react';
import { formatPrice } from '../../lib/pricing';
import { useCurrency } from '../../context/CurrencyContext';
import { useAuth } from '../../context/AuthContext';
import { track } from '../../lib/analytics';
import { SIGNUP_PATH } from '../../lib/marketing';

const FEATURES = ['Unlimited PDF downloads', 'Unlimited AI writing', 'Premium templates', 'Color themes', 'ATS score & job matching', 'LinkedIn import', '19 languages'];

export const usePlans = () => useCurrency().plans;

export const PricingCard = ({ plan, currency, highlight, onSelect, ctaLabel }) => {
  const price = plan.prices?.[currency];
  const duration = `${plan.duration} of access`;
  return (
    <div data-testid={`pricing-card-${plan.id}`} className={`relative flex flex-col rounded-2xl border p-7 sm:p-8 ${highlight ? 'border-accent bg-white shadow-[0_24px_48px_-24px_rgba(37,99,235,0.45)] ring-1 ring-accent' : 'border-border bg-white'}`}>
      {highlight && <span className="absolute -top-3 left-7 rounded-full bg-accent px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">Most popular</span>}
      <h3 className="text-lg font-bold text-primary">{plan.name}</h3>
      <div className="mt-4 flex items-baseline gap-2">
        <span data-testid={`pricing-price-${plan.id}`} className="text-[44px] font-bold leading-none tracking-tight text-primary">{formatPrice(price?.amount, currency)}</span>
        <span data-testid={`pricing-currency-${plan.id}`} className="text-sm font-medium text-muted-foreground">{currency}</span>
      </div>
      <p data-testid={`pricing-billing-${plan.id}`} className="mt-2 text-sm font-medium text-primary">One-time payment · {duration}</p>
      <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-emerald-700"><RefreshCwOff className="h-3.5 w-3.5" aria-hidden="true" /> No subscription · No auto-renewal</p>
      <ul className="mt-6 flex-1 space-y-2.5">
        {(plan.id === 'annual' ? ['Everything in 14-Day Access', 'Best value for an active job search'] : FEATURES).map((f) => (
          <li key={f} className="flex items-start gap-2 text-[15px] text-primary/85"><Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" /> {f}</li>
        ))}
      </ul>
      <button type="button" data-testid={`pricing-${plan.id}-cta`} onClick={() => onSelect(plan)} className={`mt-8 inline-flex min-h-[48px] items-center justify-center rounded-full px-5 text-[15px] font-semibold transition-[transform,background-color] hover:-translate-y-0.5 ${highlight ? 'bg-accent text-white hover:bg-[#1D4ED8]' : 'bg-primary text-white hover:bg-primary/90'}`}>{ctaLabel}</button>
    </div>
  );
};

// Data-driven plan grid shared by /pricing and the homepage. Pricing comes from /api/plans only.
export const PricingPlans = ({ onCheckout }) => {
  const plans = usePlans();
  const { currency, loading, error, refresh, paymentCurrencies } = useCurrency();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPremium = user?.plan_status === 'premium';

  const select = (plan) => {
    track('checkout_start', { plan_id: plan.id, currency });
    if (!user) { navigate(`${SIGNUP_PATH}&next=${encodeURIComponent('/pricing')}`); return; }
    if (isPremium) { navigate('/settings'); return; }
    if (onCheckout) onCheckout(plan); else navigate('/pricing');
  };

  const ordered = [...plans].sort((a, b) => (a.id === 'pass14' ? -1 : 1) - (b.id === 'pass14' ? -1 : 1));
  if (error) return <div role="alert" data-testid="pricing-load-error">Prices could not load. <button data-testid="pricing-retry-button" onClick={refresh} className="font-semibold underline">Try again</button></div>;
  if (loading || !ordered.length) return <div data-testid="pricing-loading" className="grid gap-6 sm:grid-cols-2" aria-busy="true">{[0, 1].map((i) => <div key={i} className="h-[440px] animate-pulse rounded-2xl border border-border bg-white" />)}</div>;
  return (
    <div>
      {!paymentCurrencies.includes(currency) && <p role="status" data-testid="pricing-currency-unavailable" className="mb-4 text-sm text-amber-800">Prices shown in {currency}. Checkout in this currency is currently unavailable.</p>}
      <div className="grid gap-6 sm:grid-cols-2">
      {ordered.map((p) => (
        <PricingCard key={p.id} plan={p} currency={currency} highlight={p.id === 'pass14'} onSelect={select}
          ctaLabel={isPremium ? 'Manage access' : (p.id === 'pass14' ? 'Get 14-Day Access' : 'Get Annual Access')} />
      ))}
      </div>
    </div>
  );
};
